import { ERROR_DETAILS, type ResponseErrorCode } from '@tabel/shared/errors';
import { CircleAlert, CircleCheck, X } from 'lucide-react';
import {
  type CSSProperties,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ApiError, reportClientError } from '../lib/errors';
import { useSidePanelPresence } from './ui';

/**
 * Понятный текст по коду ошибки ответа из `@tabel/shared/errors`. Тип требует текст у каждого кода:
 * новый код в общей таблице без текста здесь не пройдёт проверку типов. Коды «только в списке проверки»
 * и ошибки расчёта дня приходят не ответом, а данными экрана, — их здесь нет.
 */
export const ERROR_TEXT: Record<ResponseErrorCode | 'NETWORK', string> = {
  NETWORK: 'Нет связи с сервером',
  // 001
  VALIDATION: 'Проверьте введённые данные',
  PERIOD_INVALID: 'Неверный период табеля',
  PERIOD_TOO_LONG: 'Слишком длинный период табеля',
  HOURS_INVALID: 'Неверное число часов',
  CELL_COMMENT_REQUIRED: 'Нужен комментарий к правке',
  PERSON_NOT_IN_PERIOD: 'Человек не работает в этом периоде',
  UNAUTHENTICATED: 'Нужно войти в систему',
  BAD_CREDENTIALS: 'Неверный логин или пароль',
  ROLE_FORBIDDEN: 'Нет этой роли на выбранном объекте',
  ORIGIN_FORBIDDEN: 'Запрос отклонён: откройте табель с его адреса',
  NOT_FOUND: 'Не найдено',
  STATUS_FORBIDS_ACTION: 'В этом статусе действие недоступно',
  FUTURE_DAY: 'День ещё не наступил',
  TIMESHEET_SIGNED: 'Табель подписан, менять его нельзя',
  LOGIN_LOCKED: 'Вход временно заблокирован',
  INTERNAL: 'Ошибка сервера',
  // 002
  UNKNOWN_CUBE: 'Такого правила в системе нет. Сообщите команде разработки',
  WAVE2_MODE: 'Этот вариант система пока не умеет считать. Выберите другой вариант',
  BASIS_REQUIRED: 'Нужно основание',
  REASON_REQUIRED: 'Нужна причина',
  COMMENT_REQUIRED: 'Нужен комментарий',
  ROW_REQUIRED: 'Выберите строку табеля',
  FORBIDDEN: 'Действие не разрешено вашей роли',
  LAW_LOCKED: 'Это правило задано законом, менять его нельзя',
  FORBIDDEN_CHANNEL: 'Действие недоступно через этот канал',
  SECOND_APPROVER_REQUIRED: 'Нужен второй утверждающий',
  AGENT_DATA_DISABLED: 'Передача данных ИИ-агентам на объекте выключена',
  INSUFFICIENT_SCOPE: 'У подключения нет права на это действие',
  DRAFT_CHANGED: 'Черновик изменился, обновите страницу',
  DRAFT_NOT_EDITABLE: 'В этом состоянии черновик менять нельзя',
  NEEDS_REBUILD: 'Черновик нужно пересобрать',
  PREVIEW_STALE: 'Отчёт влияния устарел, пересчитайте его',
  GOLDEN_FAILED: 'У проверочного примера изменится результат. Поставьте флажок «Так и должно быть»',
  VALIDATION_FAILED: 'Черновик не прошёл проверку',
  IMPL_GONE: 'Реализаций этой версии нет в сборке',
  SIGN_BLOCKED: 'Подписать нельзя: есть блокирующие пометки',
  PERSON_DAY_SIGNED: 'День подписан, менять его нельзя',
  LINK_EXPIRED: 'Ссылка истекла или отозвана',
  RATE_LIMITED: 'Слишком много запросов',
  // 003
  TIME_INVALID: 'Не понятно время. Напишите, например, 19:00 или 1900',
  EXIT_BEFORE_ENTRY: 'Выход раньше входа. Проверьте время',
  QUESTION_GONE: 'Вопрос снят пересчётом. Ответ не записан',
  STALE_STATE: 'День изменился, пока была открыта форма. Проверьте новое состояние',
  QUESTIONS_OPEN: 'Есть вопросы без ответа. Правило объекта не даёт сдать табель',
  PERIOD_OVERLAP: 'Период пересекается с другим табелем этой бригады',
  UNDO_NOT_LAST: 'Отменить можно только последнее действие',
  UNDO_STALE: 'Отменить нельзя: после действия данные изменились',
  UNDO_EXPIRED: 'Время на отмену прошло',
  // 004 (тексты выверяет задача T068; ревью T103)
  VALID_FROM_TOO_EARLY:
    'Дата начала слишком ранняя. Выберите дату не раньше начала последней опубликованной версии',
  DRAFT_LED_BY_OTHER: 'Черновик уже ведёт другой администратор',
  DRAFT_OPEN: 'Сначала опубликуйте черновик или отмените его',
  NOTHING_TO_PUBLISH: 'В черновике нет изменений',
  RISK_NOT_ACKNOWLEDGED: 'Поставьте флажок «Понимаю риск» у каждого предупреждения',
  RISK_CHANGED: 'Предупреждение изменилось. Прочитайте его снова',
  CALC_ERRORS: 'Расчёт по черновику даёт ошибку в некоторых днях',
  IMPACT_SUPERSEDED: 'Влияние посчитано заново. Смотрите новые числа',
  RECALC_IN_PROGRESS: 'Табель пересчитывается по новым правилам. Подпишите, когда пересчёт закончится',
  VERSION_NOT_FUTURE: 'Эти правила уже действуют',
  VERSION_NOT_PAST: 'Вернуть можно только правила, которые действовали раньше',
  CALC_FAILED: 'Сбой расчёта. Сообщите код ошибки команде разработки',
  // 005
  AGENT_RIGHT_MISSING: 'Подключать ИИ-агента может только пользователь с правом «ИИ-агент»',
  SKILL_VERSION_MISMATCH: 'Версия скилла агента не совпадает с версией сервера. Обновите скилл',
  PLAN_BLOCKED: 'План нельзя заморозить: в черновике есть ошибки',
  PLAN_STALE: 'План устарел. Попросите агента заморозить план заново',
  CLIENT_NOT_ALLOWED: 'Для этого клиента агента выборки дней не разрешены',
  PERSONAL_DATA: 'В тексте есть ФИО или номер человека. Уберите их',
  RULES_UNAVAILABLE: 'Расчёт по правилам на сервере выключен',
  // 006
  ENROLLMENT_INVALID: 'Ключ устарел. Нажмите «Подключить компьютер» ещё раз',
  ENROLLMENT_USED: 'Ключ уже использован. Нажмите «Подключить компьютер» ещё раз',
  CONNECTOR_REVOKED: 'Компьютер отключён от табеля',
  CONNECTOR_OFFLINE: 'Компьютер не в сети. Агент ответит, когда компьютер подключится',
  AGENT_NOT_READY: 'Агент не готов. Откройте «Мои ИИ-клиенты» и проверьте агента',
  INSTALL_RUNNING: 'Установка уже идёт. Дождитесь конца установки',
  INTERACTION_CLOSED: 'Запрос агента уже не ждёт ответа',
  TURN_NOT_RUNNING: 'Агент сейчас не работает',
  THREAD_ARCHIVED: 'Разговор в архиве. Писать в него нельзя',
  // 007 (площадки; specs/007-sites contracts/http-api.md).
  SITE_NAME_TAKEN: 'Площадка с таким названием уже есть. Введите другое название',
  SITE_CODE_TAKEN: 'Такой код уже занят. Введите другой код площадки',
  CITY_UNKNOWN: 'Выберите город из списка или укажите часовой пояс',
  SECRET_EXPIRED: 'Шаг уже выполнен, поэтому значение больше не показывается. Выдайте новое',
  ADDRESS_NOT_SEEN: 'С этого адреса SmartPSS не пробовал войти. Проверьте адрес',
  STEPS_NOT_READY: 'Сначала выполните нужные шаги подключения',
  SITE_REVISION_CONFLICT: 'Площадку изменили в другом окне. Обновите страницу',
  CONFIRM_NAME_MISMATCH: 'Название не совпадает. Введите название площадки точно',
  UPLOAD_INVALID: 'Файл не удалось разобрать. Проверьте, что это выгрузка из SmartPSS',
  UPLOAD_TOO_LARGE: 'Файл слишком большой: больше 20 МБ или 200 000 строк',
  WATCH_TOO_OFTEN: 'Проверка уже идёт. Повторите через 30 секунд',
  RISK_NOT_ALLOWED: 'Продолжить с риском можно только у шага с предупреждением',
  EXTRACT_CONFIRM_NOT_ALLOWED: 'Подтвердить вручную можно после двух утр без выгрузки',
  OPS_UNAVAILABLE: 'Сервер подключения площадок не отвечает. Повторите позже',
  RULES_NOT_PREPARED: 'Правил у площадки пока нет. Их готовят в шаге 10 подключения площадки',
  RULES_NOT_ON_DATE: 'Правил на эту дату нет',
  LAUNCH_RULES: 'Правила к запуску начнут действовать при запуске площадки, в шаге 10',
};

function textOf(code: string): string | undefined {
  return Object.hasOwn(ERROR_TEXT, code) ? ERROR_TEXT[code as keyof typeof ERROR_TEXT] : undefined;
}

/** `2026-10-16` → `16.10.2026`. */
const ruDate = (iso: string) => iso.split('-').reverse().join('.');

/**
 * Тексты кодов 004 с подстановкой из `details` ответа (specs/004 contracts/http-api.md, «Коды ошибок»): кто ведёт
 * черновик, с какой даты можно начать, сколько дней с ошибкой. `details` не по схеме — общий текст кода.
 */
const DETAIL_TEXT: Partial<Record<ResponseErrorCode, (details: unknown) => string | undefined>> = {
  DRAFT_LED_BY_OTHER: (d) => {
    const p = ERROR_DETAILS.DRAFT_LED_BY_OTHER.safeParse(d);
    return p.success ? `Черновик уже ведёт ${p.data.leaderName}` : undefined;
  },
  VALID_FROM_TOO_EARLY: (d) => {
    const p = ERROR_DETAILS.VALID_FROM_TOO_EARLY.safeParse(d);
    return p.success
      ? `Дата начала слишком ранняя. Выберите ${ruDate(p.data.earliest)} или позже`
      : undefined;
  },
  CALC_ERRORS: (d) => {
    const p = ERROR_DETAILS.CALC_ERRORS.safeParse(d);
    return p.success ? `Расчёт по черновику даёт ошибку. Дней с ошибкой: ${p.data.days.length}` : undefined;
  },
};

function detailText(e: ApiError): string | undefined {
  return e.code === 'NETWORK' ? undefined : DETAIL_TEXT[e.code]?.(e.details);
}

/**
 * Коды, у которых сервер называет точную причину словами (`message`): «На этот день стоит код отсутствия: …» (T105),
 * «Слишком много расчётов подряд. Повторите через минуту» (T121). Она важнее общего текста кода.
 * Коды 005 (итоговое ревью T160; specs/005-rules-mcp/contracts/mcp-tools.md, «Ошибки»; contracts/http-api.md) — тоже:
 * сервер говорит, что именно случилось с этим действием («Передать день агенту может только пользователь с правом
 * «ИИ-агент»», «Дни для агента на сегодня закончились»), а текст кода — общий для всех действий.
 * Служебная проверка тела запроса («Неверные данные: поле», «Неверный запрос», api/src/lib/errors.ts) и запасной текст
 * интерфейса без ответа сервера («Ошибка 403», «Ошибка сервера») — не для человека: тогда текст кода.
 */
const SERVER_WORDS: ReadonlySet<string> = new Set<ResponseErrorCode>([
  'VALIDATION',
  'RATE_LIMITED',
  'AGENT_RIGHT_MISSING',
  'SKILL_VERSION_MISMATCH',
  'PLAN_BLOCKED',
  'PLAN_STALE',
  'CLIENT_NOT_ALLOWED',
  'PERSONAL_DATA',
  'RULES_UNAVAILABLE',
  'AGENT_DATA_DISABLED',
  'LINK_EXPIRED',
  'INSUFFICIENT_SCOPE',
  // 007: сервер называет номера шагов и точную причину.
  'STEPS_NOT_READY',
  'UPLOAD_INVALID',
  'OPS_UNAVAILABLE',
  // Поправка 20.10.2026: сервер называет дату начала первой версии правил.
  'RULES_NOT_ON_DATE',
]);
const SERVICE_CHECK = /^Неверн(ые данные|ый запрос)|^Ошибка( \d{3}| сервера)?$/u;

function serverWords(e: ApiError): string | null {
  const m = e.message?.trim();
  return SERVER_WORDS.has(e.code) && m && !SERVICE_CHECK.test(m) && m !== textOf(e.code) ? m : null;
}

/**
 * Понятный текст ошибки ответа — для тоста и плашек в панели: точная причина сервера (коды выше), иначе текст кода,
 * иначе сообщение сервера. Не ошибка ответа — `fallback`.
 */
export function errorText(err: unknown, fallback = 'Неожиданная ошибка'): string {
  if (!(err instanceof ApiError)) return fallback;
  return serverWords(err) ?? detailText(err) ?? textOf(err.code) ?? err.message;
}

/** `errorText` предложением: точка в конце, если её нет (слова сервера приходят и с точкой, и без неё). */
export function errorSentence(err: unknown, fallback?: string): string {
  const t = errorText(err, fallback).trim();
  return /[.!?…]$/u.test(t) ? t : `${t}.`;
}

/**
 * Код ответа для показа рядом с кодом ошибки (requestId), мелко: когда текст ошибки — слова сервера, по нему одному не
 * видно код. Текст кода код уже называет — тогда `null`. Код — для команды разработки, не часть текста для человека.
 */
export function errorCode(err: unknown): string | null {
  return err instanceof ApiError && serverWords(err) !== null ? err.code : null;
}

/** Кнопка в тосте: «Отменить» после обратимого действия, «Открыть» после создания. */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface InfoOptions {
  /** Одно действие: «Отменить». Тост с действием живёт 6 с. */
  action?: ToastAction;
  /** Время показа, мс. По умолчанию 4 с, с действием — 6 с (дизайн-система, Toast). */
  duration?: number;
  /** Вторая строка серым: подробности итога («Пропущены: …»). */
  detail?: string;
}

export interface ErrorOptions {
  /** Экран и действие — для журнала ошибок интерфейса. */
  screen: string;
  action: string;
  /** «Повторить» — повторяет ту же операцию. */
  retry?: () => void;
  /** Начало текста: «Не сохранено» → «Не сохранено: нет связи с сервером.» */
  lead?: string;
  /** Пояснение после причины: «Введённое осталось в панели.» */
  note?: string;
}

interface Toast {
  id: number;
  /** Текст по коду; у неизвестного кода и сбоя интерфейса — само сообщение. */
  title: string;
  /** Сообщение сервера, если оно добавляет подробности к тексту кода. */
  detail: string | null;
  retryAfterSec: number | null;
  /** Код ошибки для команды разработки — requestId. */
  code: string | null;
  /** Код ответа рядом с кодом ошибки, когда текст — слова сервера (`errorCode`). */
  errorCode: string | null;
  kind: 'error' | 'info';
  action: ToastAction | null;
  duration: number;
}

export interface ToastApi {
  /** Ошибка: текст по коду, сообщение сервера, «Код ошибки» (requestId), «Повторить», если передан `retry`. */
  showError: (err: unknown, opts?: ErrorOptions) => void;
  /** Итог действия: «Ответ сохранён», с действием — «Ответ сохранён · Отменить». */
  showInfo: (message: string, opts?: InfoOptions) => void;
}

const ToastContext = createContext<ToastApi>({ showError: () => {}, showInfo: () => {} });

let seq = 0;

const INFO_MS = 4000;
const ACTION_MS = 6000;
const ERROR_MS = 12000;

/** Первая буква — строчная после «Не сохранено:», если это не сокращение («СКУД»). */
function lowerFirst(s: string): string {
  const [a, b] = s;
  return a && b && b === b.toLowerCase() ? a.toLowerCase() + s.slice(1) : s;
}
const sentence = (s: string) => (/[.!?…]$/.test(s) ? s : `${s}.`);

function compose(text: string, lead?: string, note?: string): string {
  if (!lead && !note) return text;
  const main = lead ? `${lead}: ${sentence(lowerFirst(text))}` : sentence(text);
  return note ? `${main} ${note}` : main;
}

/**
 * Тосты по дизайн-системе (Toast; contracts/ui-panel.md, «Тосты»): тёмная плашка внизу справа, на телефоне — во всю
 * ширину над нижней навигацией. Состав: иконка → текст → одно действие → ✕. Итог — `role="status"`, ошибка —
 * `role="alert"` с «Кодом ошибки» (requestId, FR-032) и «Повторить». Наведение мыши или фокус держат тост.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const remove = useCallback((id: number) => setToasts((all) => all.filter((x) => x.id !== id)), []);
  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = ++seq;
    setToasts((all) => [...all, { ...t, id }]);
  }, []);
  const showError = useCallback(
    (err: unknown, opts?: ErrorOptions) => {
      const action = opts?.retry ? { label: 'Повторить', onClick: opts.retry } : null;
      const where = { screen: opts?.screen ?? 'unknown', action: opts?.action ?? 'unknown' };
      if (err instanceof ApiError) {
        const words = serverWords(err);
        const text = errorText(err);
        const detail = err.message && err.message !== text ? err.message : null;
        push({
          title: compose(text, opts?.lead, opts?.note),
          detail,
          // Сервер сам сказал, когда повторить («Повторите через минуту»), — вторая строка не нужна.
          retryAfterSec: words?.includes('Повторите') ? null : (err.retryAfterSec ?? null),
          code: err.requestId,
          errorCode: errorCode(err),
          kind: 'error',
          action,
          duration: ERROR_MS,
        });
        if (err.status >= 500 || err.code === 'NETWORK') reportClientError(where.screen, where.action, err);
      } else {
        const title = compose('Неожиданная ошибка интерфейса', opts?.lead, opts?.note);
        push({
          title,
          detail: null,
          retryAfterSec: null,
          code: null,
          errorCode: null,
          kind: 'error',
          action,
          duration: ERROR_MS,
        });
        reportClientError(where.screen, where.action, err);
      }
    },
    [push],
  );
  const showInfo = useCallback(
    (message: string, opts?: InfoOptions) =>
      push({
        title: message,
        detail: opts?.detail ?? null,
        retryAfterSec: null,
        code: null,
        errorCode: null,
        kind: 'info',
        action: opts?.action ?? null,
        duration: opts?.duration ?? (opts?.action ? ACTION_MS : INFO_MS),
      }),
    [push],
  );
  const api = useMemo(() => ({ showError, showInfo }), [showError, showInfo]);
  // Тост не закрывает подвал открытой правой панели (кнопки «Сохранить …»): справа — левее панели; на весь экран
  // (до 900 px) — над подвалом; «Развернуть» — над подвалом развёрнутой панели.
  const panel = useSidePanelPresence();
  const place =
    panel?.mode === 'side'
      ? 'right-[500px] bottom-5 max-w-[calc(100vw-520px)] items-end'
      : panel?.mode === 'screen'
        ? 'inset-x-4 items-stretch'
        : 'right-5 bottom-5 max-w-[calc(100vw-40px)] items-end max-sm:inset-x-4 max-sm:bottom-[calc(56px+16px+env(safe-area-inset-bottom))] max-sm:max-w-none max-sm:items-stretch';
  const style: CSSProperties | undefined =
    panel?.mode === 'screen'
      ? { bottom: `${Math.max(panel.footer, 56) + 8}px` }
      : panel?.mode === 'expanded'
        ? { bottom: `calc(5vh + ${Math.max(panel.footer, 56) + 8}px)` }
        : undefined;
  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Над правой панелью на весь экран (z 80) и окнами страницы. */}
      <div
        data-panel={panel?.mode}
        style={style}
        className={`pointer-events-none fixed z-[100] flex flex-col gap-2 ${place}`}
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={remove} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Значение строки «Код ошибки:»: requestId и, если текст ошибки — слова сервера, код ответа: «req-1 · PLAN_STALE».
 * Одна разметка для тоста и плашек окон и страниц 005. Подпись «Код ошибки:» ставит место показа.
 */
export function ErrorCodes({
  requestId,
  code,
  className = '',
}: {
  requestId: string | null;
  code: string | null;
  className?: string;
}) {
  return (
    <>
      {requestId && <span className={`font-mono select-all ${className}`}>{requestId}</span>}
      {requestId && code && ' · '}
      {code && <span className={`font-mono ${className}`}>{code}</span>}
    </>
  );
}

const TOAST_BTN =
  'shrink-0 rounded-[6px] px-2 py-1.5 text-[13px] font-semibold leading-none transition-colors hover:bg-ink-2 max-sm:min-h-10';

function ToastItem({ toast: t, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const [held, setHeld] = useState(false);
  useEffect(() => {
    if (held) return;
    const timer = setTimeout(() => onDismiss(t.id), t.duration);
    return () => clearTimeout(timer);
  }, [held, t.id, t.duration, onDismiss]);
  const error = t.kind === 'error';
  const Icon = error ? CircleAlert : CircleCheck;
  return (
    <div
      role={error ? 'alert' : 'status'}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
      className="pointer-events-auto flex max-w-[420px] items-start gap-2.5 rounded-control bg-ink py-1.5 pr-1.5 pl-3 text-[13px] text-surface shadow-pop transition-[opacity,translate] duration-200 ease-spring starting:translate-y-3.5 starting:opacity-0 motion-reduce:transition-none max-sm:max-w-none"
    >
      <Icon
        size={16}
        strokeWidth={1.75}
        className={`mt-[7px] shrink-0 ${error ? 'text-bad-soft' : 'text-weekend-strong'}`}
        aria-hidden
      />
      <div className="min-w-0 flex-1 py-1.5 leading-[1.4] text-pretty">
        <div>{t.title}</div>
        {t.detail && <div className="mt-0.5 text-ink-3">{t.detail}</div>}
        {t.retryAfterSec !== null && (
          <div className="mt-0.5 text-ink-3">Повторите через {t.retryAfterSec} с.</div>
        )}
        {(t.code || t.errorCode) && (
          <div className="mt-1 text-[12px] text-ink-3">
            Код ошибки: <ErrorCodes requestId={t.code} code={t.errorCode} className="text-line" />
          </div>
        )}
      </div>
      {t.action && (
        <button
          type="button"
          className={TOAST_BTN}
          onClick={() => {
            onDismiss(t.id);
            t.action?.onClick();
          }}
        >
          {t.action.label}
        </button>
      )}
      <button
        type="button"
        aria-label="Закрыть"
        title="Закрыть"
        className={`${TOAST_BTN} text-ink-3 hover:text-surface max-sm:min-w-10`}
        onClick={() => onDismiss(t.id)}
      >
        <X size={14} strokeWidth={1.75} className="mx-auto" aria-hidden />
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  return useContext(ToastContext);
}
